import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  PitchContextFieldLabel,
  PITCH_CONTEXT_FIELD_PLACEHOLDER,
} from "../brand/PitchContextFieldLabel";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import { brandProfileService } from "../../services/brandProfile";

function brandOnboardingRequiredLabel(text) {
  return (
    <span className="inline-flex items-baseline gap-0.5">
      <span>{text}</span>
      <span className="text-[10px] font-normal normal-case text-theme-accent leading-none" aria-hidden="true">
        *
      </span>
    </span>
  );
}

function tabLabel(draft) {
  const name = (draft.form.company_name || "").trim();
  if (name) return name.length > 22 ? `${name.slice(0, 20)}…` : name;
  const url = (draft.form.website_url || "").trim();
  if (url) return url.length > 22 ? `${url.slice(0, 20)}…` : url;
  return "Brand";
}

const BRAND_DETAILS_TEXTAREA_ROWS = 3;

export function OnboardingBrandStep({
  brandDrafts,
  activeClientId,
  onSelectBrand,
  activeForm,
  updateActiveForm,
  onRemoveBrand,
  removeBrandPending,
  onAppendBrandDraft,
  addBrandRequestSignal,
  brandSubstep,
  brandPreviewMutation,
  onBrandUrlContinue,
  onNoBrandWebsite,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const addUrlInputRef = useRef(null);
  const [addForm, setAddForm] = useState({
    company_name: "",
    website_url: "",
    core_offering: "",
    icp: "",
    custom_context: "",
    brand_context: {},
  });

  const addPrefillMutation = useMutation({
    mutationFn: (websiteUrl) => brandProfileService.prefillFromWebsite(websiteUrl),
    onSuccess: (data) => {
      const p = data?.prefill || {};
      const snippet = (data?.webpage_content && String(data.webpage_content).trim()) || "";
      const form = {
        website_url: data?.website_url || "",
        company_name: p.company_name ?? "",
        core_offering: p.core_offering ?? "",
        icp: p.icp ?? "",
        custom_context: "",
        brand_context: snippet ? { webpage_content: data.webpage_content } : {},
      };
      onAppendBrandDraft(form);
      closeAddModal();
    },
    onError: (err, websiteUrl) => {
      const body = err?.response?.data;
      const canonical = body?.website_url;
      onAppendBrandDraft({
        website_url: typeof canonical === "string" ? canonical : websiteUrl || "",
        company_name: "",
        core_offering: "",
        icp: "",
        custom_context: "",
        brand_context: {},
      });
      closeAddModal();
    },
  });

  const closeAddModal = () => {
    setAddOpen(false);
    addPrefillMutation.reset();
    setAddForm({
      company_name: "",
      website_url: "",
      core_offering: "",
      icp: "",
      custom_context: "",
      brand_context: {},
    });
  };

  const handleAddContinue = () => {
    const raw = addForm.website_url.trim();
    if (!raw || addPrefillMutation.isPending) return;
    addPrefillMutation.mutate(raw);
  };

  const handleAddNoWebsite = () => {
    onAppendBrandDraft({
      company_name: "",
      website_url: "",
      core_offering: "",
      icp: "",
      custom_context: "",
      brand_context: {},
    });
    closeAddModal();
  };

  useEffect(() => {
    if (addBrandRequestSignal > 0) {
      setAddOpen(true);
    }
  }, [addBrandRequestSignal]);

  useEffect(() => {
    if (!addOpen) return;
    const id = requestAnimationFrame(() => {
      addUrlInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [addOpen]);

  const multi = brandDrafts.length > 1;

  return (
    <section>
      <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background mb-2">
        Tell us about your company
      </h1>
      <p className="font-mono text-[12px] text-theme-muted mb-8">
        {brandSubstep === "url"
          ? "We'll use this to define your Ideal Customer Profile (ICP) and surface relevant leads."
          : "Fine tune your company profile, or add more. You can edit this later in settings."}
      </p>

      {multi && brandSubstep === "details" ? (
        <div className="flex flex-wrap items-center gap-2 mb-4" role="tablist" aria-label="Brands">
          {brandDrafts.map((d) => {
            const selected = d.clientId === activeClientId;
            return (
              <div key={d.clientId} className="inline-flex items-center gap-1">
                <button
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`font-mono text-[11px] px-3 py-1.5 rounded border transition-colors ${
                    selected
                      ? "border-theme-secondary bg-theme-secondary text-theme-text"
                      : "border-theme-secondary border-opacity-40 text-theme-muted hover:text-theme-text hover:border-opacity-70"
                  }`}
                  onClick={() => onSelectBrand(d.clientId)}
                >
                  {tabLabel(d)}
                </button>
                <button
                  type="button"
                  className="font-mono text-[10px] text-theme-muted hover:text-theme-accent px-1 py-0.5 disabled:opacity-40"
                  aria-label="Remove brand"
                  disabled={removeBrandPending}
                  onClick={() => onRemoveBrand(d.clientId)}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      ) : null}

      <Card>
        <div className="grid grid-cols-1 gap-4">
          {brandSubstep === "url" ? (
            <form
              className="contents"
              onSubmit={(e) => {
                e.preventDefault();
                if (!activeForm.website_url.trim() || brandPreviewMutation.isPending) return;
                onBrandUrlContinue();
              }}
            >
              <Input
                label="Website URL"
                placeholder="yoururl.com"
                value={activeForm.website_url}
                onChange={(e) => updateActiveForm({ website_url: e.target.value })}
              />
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <Button type="button" variant="ghost" size="sm" onClick={onNoBrandWebsite}>
                  I don't have a website yet
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  loading={brandPreviewMutation.isPending}
                  disabled={!activeForm.website_url.trim() || brandPreviewMutation.isPending}
                >
                  Continue
                </Button>
              </div>
            </form>
          ) : (
            <>
              <Input
                label={brandOnboardingRequiredLabel("Company name")}
                placeholder="e.g. FlipMail"
                value={activeForm.company_name}
                onChange={(e) => updateActiveForm({ company_name: e.target.value })}
              />
              <Input
                label={brandOnboardingRequiredLabel("Product or service")}
                multiline
                rows={BRAND_DETAILS_TEXTAREA_ROWS}
                placeholder="What you deliver and for whom: the problem, the outcome, and what is different about you. Use this for a product, a service, or a mix of both."
                value={activeForm.core_offering}
                onChange={(e) => updateActiveForm({ core_offering: e.target.value })}
              />
              <Input
                label={brandOnboardingRequiredLabel("Ideal customer profile")}
                multiline
                rows={BRAND_DETAILS_TEXTAREA_ROWS}
                placeholder="Signals of a strong lead: industries, company size or stage, buyer roles, regions, tech stack, or anything else you want us to treat as a fit."
                value={activeForm.icp}
                onChange={(e) => updateActiveForm({ icp: e.target.value })}
              />
              <Input
                label={<PitchContextFieldLabel />}
                multiline
                rows={BRAND_DETAILS_TEXTAREA_ROWS}
                placeholder={PITCH_CONTEXT_FIELD_PLACEHOLDER}
                value={activeForm.custom_context}
                onChange={(e) => updateActiveForm({ custom_context: e.target.value })}
              />
            </>
          )}
        </div>
      </Card>

      {addOpen ? (
        <Modal open onClose={closeAddModal} dialogClassName="w-full max-w-lg">
          <Card className="p-6 relative">
            <button
              type="button"
              className="absolute top-4 right-4 text-theme-muted hover:text-theme-text font-mono text-xs"
              onClick={closeAddModal}
            >
              Close
            </button>
            <h2 className="font-display font-bold italic text-xl text-theme-on-background mb-1 pr-16">Add a brand</h2>
            <p className="font-mono text-[11px] text-theme-muted mb-5">
              Enter a website to prefill. We&apos;ll fill in the details for your review.
            </p>
            <form
              className="grid grid-cols-1 gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddContinue();
              }}
            >
              <Input
                ref={addUrlInputRef}
                label="Website URL"
                placeholder="yoururl.com"
                value={addForm.website_url}
                onChange={(e) => setAddForm((f) => ({ ...f, website_url: e.target.value }))}
              />
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <Button type="button" variant="ghost" size="sm" onClick={handleAddNoWebsite}>
                  {"I don't have a website yet"}
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  loading={addPrefillMutation.isPending}
                  disabled={!addForm.website_url.trim() || addPrefillMutation.isPending}
                >
                  Continue
                </Button>
              </div>
            </form>
          </Card>
        </Modal>
      ) : null}
    </section>
  );
}
